import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import { buildCodeGeneratorPreview, CODE_GENERATOR_SUPPORTED_ENTITIES, getCodeGeneratorEntityLabelId } from './definitions';

// lazy loaded components
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
const CodeGeneratorEntitySelect = lazy(() => import('ui-component/fields/CodeGeneratorEntitySelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => [
                ...appGridActions(capabilities, params?.row, permissionData, onClickActions)
            ]
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'className',
            type: 'singleSelect',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip
                        title={
                            <FormattedMessage id={getCodeGeneratorEntityLabelId(params.value) || 'entity-admin-codegeneratorsettings'} />
                        }
                    >
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={getCodeGeneratorEntityLabelId(params.value) || 'entity-admin-codegeneratorsettings'} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-admin-codegeneratorsettings-classname'
        },
        {
            field: 'data',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            flex: 1,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={buildCodeGeneratorPreview(params.value)}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {buildCodeGeneratorPreview(params.value)}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-admin-codegeneratorsettings-data'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat',
            hide: false
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                className: true,
                data: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false
            },
            orderedFields: ['actions', 'id', 'className', 'data', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                className: { width: 240 },
                data: { width: 320 },
                createdAt: { width: 150 },
                createdBy: { width: 200 },
                updatedAt: { width: 150 },
                updatedBy: { width: 200 }
            }
        },
        sorting: {
            sortModel: [{ field: 'className', sort: 'asc' }]
        },
        toolbarFilters: {}
    },
    filterDef: [
        {
            filterType: 'array',
            field: { fieldName: 'className', fieldType: 'simple' },
            label: 'entity-admin-codegeneratorsettings-classname',
            renderField: (params) => (
                <CodeGeneratorEntitySelect
                    key="filterCodeGeneratorSettings-className"
                    availableOptions={CODE_GENERATOR_SUPPORTED_ENTITIES}
                    multiple
                    {...params}
                />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterCodeGeneratorSettings-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterCodeGeneratorSettings-updatedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-codegeneratorsettings',
        fields: [{ fieldName: 'className', fieldType: 'simple' }]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'className', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' }
    ]
};

export default initialState;
